/**
 * Dropdown mit Suchfeld, gemeinsam genutzt von Toolbar (Schriftart, Profilfelder)
 * und den Tabellen-Dialogen. Gesucht wird an jeder Stelle des Namens, Treffer
 * werden hervorgehoben; Pfeiltasten und Enter bedienen die Liste.
 */

export interface SearchableOption {
  value: string;
  label: string;
  /** Zusatzzeichen hinter dem Namen, z. B. „•“ für befüllte Profilfelder. */
  suffix?: string;
  /** Tooltip am Eintrag. */
  hint?: string;
  /** Der Eintrag zeigt sich in dieser Schrift (Vorschau im Schriftart-Menü). */
  previewFont?: string;
}

export interface SearchableDropdown {
  element: HTMLElement;
  /** Beschriftung der Schaltfläche, ohne den Inhalt zu verändern. */
  setTriggerLabel: (label: string) => void;
  /** Hebt den Eintrag hervor, dessen Wert das Prädikat erfüllt. */
  setActiveOption: (isActive: (value: string) => boolean) => void;
}

export function createSearchableDropdown(config: {
  title: string;
  searchPlaceholder: string;
  /** Zusatzklasse am Rahmen für Breiten-Feinheiten. */
  extraClass: string;
  triggerLabel: string;
  options: SearchableOption[];
  onPick: (value: string) => void;
  /** Vor dem Öffnen, z. B. um die Auswahl im Editor zu sichern. */
  onBeforeOpen?: () => void;
}): SearchableDropdown {
  const wrap = document.createElement("div");
  wrap.className = `sunnysign-editor-fontsize sunnysign-editor-searchlist ${config.extraClass}`;
  wrap.title = config.title;

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "sunnysign-editor-fontsize__trigger sunnysign-editor-searchlist__trigger";
  trigger.setAttribute("aria-haspopup", "listbox");
  trigger.setAttribute("aria-expanded", "false");
  trigger.setAttribute("aria-label", config.title);
  const triggerLabel = document.createElement("span");
  triggerLabel.className = "sunnysign-editor-searchlist__value";
  triggerLabel.textContent = config.triggerLabel;
  const caret = document.createElement("span");
  caret.className = "sunnysign-editor-searchlist__caret";
  caret.setAttribute("aria-hidden", "true");
  trigger.appendChild(triggerLabel);
  trigger.appendChild(caret);

  const panel = document.createElement("div");
  panel.className = "sunnysign-editor-fontsize__panel sunnysign-editor-searchlist__panel";
  panel.hidden = true;

  const search = document.createElement("input");
  search.type = "text";
  search.className = "sunnysign-editor-searchlist__search";
  search.placeholder = config.searchPlaceholder;
  search.setAttribute("aria-label", config.searchPlaceholder);
  search.autocomplete = "off";
  search.spellcheck = false;

  const list = document.createElement("div");
  list.className = "sunnysign-editor-searchlist__options";
  list.setAttribute("role", "listbox");

  /** Name mit hervorgehobenen Treffern; Textknoten statt innerHTML wegen Namen mit Sonderzeichen. */
  const renderLabel = (target: HTMLElement, option: SearchableOption, query: string): void => {
    target.textContent = "";
    const label = option.label;
    if (!query) {
      target.appendChild(document.createTextNode(label));
    } else {
      const haystack = label.toLowerCase();
      const needle = query.toLowerCase();
      let index = 0;
      while (index <= label.length) {
        const hit = haystack.indexOf(needle, index);
        if (hit < 0) {
          target.appendChild(document.createTextNode(label.slice(index)));
          break;
        }
        if (hit > index) target.appendChild(document.createTextNode(label.slice(index, hit)));
        const mark = document.createElement("mark");
        mark.className = "sunnysign-editor-searchlist__match";
        mark.textContent = label.slice(hit, hit + needle.length);
        target.appendChild(mark);
        index = hit + needle.length;
      }
    }
    if (option.suffix) target.appendChild(document.createTextNode(` ${option.suffix}`));
  };

  const options = config.options.map((option) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "sunnysign-editor-fontsize__option sunnysign-editor-searchlist__option";
    button.setAttribute("role", "option");
    button.dataset.value = option.value;
    if (option.hint) button.title = option.hint;
    if (option.previewFont) button.style.fontFamily = option.previewFont;
    button.addEventListener("mousedown", (event) => event.preventDefault());
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      pick(option);
    });
    list.appendChild(button);
    return { option, button };
  });

  const visibleOptions = (): typeof options => options.filter((entry) => !entry.button.hidden);

  /** Index der Tastatur-Hervorhebung innerhalb der sichtbaren Treffer (−1 = keine). */
  let keyboardIndex = -1;

  const clearKeyboardFocus = (): void => {
    options.forEach((entry) => entry.button.classList.remove("is-keyboard-focus"));
    keyboardIndex = -1;
  };

  const setKeyboardFocus = (index: number): void => {
    const visible = visibleOptions();
    options.forEach((entry) => entry.button.classList.remove("is-keyboard-focus"));
    if (visible.length === 0) {
      keyboardIndex = -1;
      return;
    }
    keyboardIndex = ((index % visible.length) + visible.length) % visible.length;
    const entry = visible[keyboardIndex];
    entry.button.classList.add("is-keyboard-focus");
    entry.button.scrollIntoView({ block: "nearest" });
  };

  const onDocPointer = (event: Event): void => {
    if (wrap.contains(event.target as Node)) return;
    setOpen(false);
  };

  const setOpen = (open: boolean): void => {
    panel.hidden = !open;
    trigger.setAttribute("aria-expanded", open ? "true" : "false");
    wrap.classList.toggle("is-open", open);
    if (open) {
      document.addEventListener("mousedown", onDocPointer);
      document.addEventListener("touchstart", onDocPointer, { passive: true });
    } else {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
      clearKeyboardFocus();
    }
  };

  const applyFilter = (): void => {
    const query = search.value.trim();
    const needle = query.toLowerCase();
    options.forEach((entry) => {
      const visible = !needle || entry.option.label.toLowerCase().includes(needle);
      entry.button.hidden = !visible;
      if (visible) renderLabel(entry.button, entry.option, query);
    });
    // Nach Filterwechsel die Tastatur-Hervorhebung zurücksetzen.
    clearKeyboardFocus();
  };

  const setActiveOption = (isActive: (value: string) => boolean): void => {
    options.forEach((entry) => {
      const active = isActive(entry.option.value);
      entry.button.classList.toggle("is-active", active);
      entry.button.setAttribute("aria-selected", active ? "true" : "false");
    });
  };

  const pick = (option: SearchableOption): void => {
    setOpen(false);
    config.onPick(option.value);
  };

  search.addEventListener("mousedown", (event) => event.stopPropagation());
  search.addEventListener("click", (event) => event.stopPropagation());
  search.addEventListener("input", applyFilter);
  search.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setKeyboardFocus(keyboardIndex < 0 ? 0 : keyboardIndex + 1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      const visible = visibleOptions();
      setKeyboardFocus(keyboardIndex < 0 ? visible.length - 1 : keyboardIndex - 1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const visible = visibleOptions();
      const chosen = keyboardIndex >= 0 ? visible[keyboardIndex] : visible[0];
      if (chosen) pick(chosen.option);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  });

  trigger.addEventListener("mousedown", (event) => {
    event.preventDefault();
    config.onBeforeOpen?.();
  });
  trigger.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    const willOpen = !!panel.hidden;
    setOpen(willOpen);
    if (!willOpen) return;
    search.value = "";
    applyFilter();
    const active = options.find((entry) => entry.button.classList.contains("is-active"));
    active?.button.scrollIntoView({ block: "nearest" });
    setTimeout(() => search.focus(), 0);
  });

  panel.appendChild(search);
  panel.appendChild(list);
  wrap.appendChild(trigger);
  wrap.appendChild(panel);
  applyFilter();
  return {
    element: wrap,
    setTriggerLabel: (label: string) => {
      triggerLabel.textContent = label;
    },
    setActiveOption,
  };
}
